import {
  Box,
  HStack,
  Icon,
  IconButton,
  Image,
  Link,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Skeleton,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { mdiCalendarOutline, mdiInformationOutline } from '@mdi/js';
import { format, parseISO } from 'date-fns';
import { getGoLiveDynamicText } from 'features/common/utils/goLiveText';

interface IGoLiveCardProps {
  goLiveDate?: {
    goLiveDate: string;
    dateUpdatedBy: string;
  } | null;
  loading: boolean;
}

export const GoLiveCard = ({ goLiveDate, loading }: IGoLiveCardProps) => {
  const formattedDate = goLiveDate
    ? format(parseISO(goLiveDate?.goLiveDate), 'MMMM do, yyyy')
    : '';
  const submittedBy = goLiveDate?.dateUpdatedBy ?? '';

  const dynamicText = goLiveDate
    ? getGoLiveDynamicText(goLiveDate.goLiveDate)
    : '';

  return (
    <Box
      w='full'
      bg='white'
      _dark={{ bg: 'gray.800', borderColor: 'gray.600' }}
      borderRadius='lg'
      border='1px solid'
      borderColor='gray.200'
      p='4'
      data-testid='go-live-card'
    >
      <VStack align='stretch' spacing='3'>
        <HStack justify='space-between' align='flex-start'>
          <HStack spacing='2'>
            <Image
              src='https://delivery-sitecore.sitecorecontenthub.cloud/api/public/content/spot-rocket-launch'
              boxSize='8'
              alt='Go-live date'
            />
            <Text fontWeight='bold' fontSize='md'>
              Go-live date
            </Text>
          </HStack>
          <Popover placement='bottom-end' isLazy>
            <PopoverTrigger>
              <IconButton
                aria-label='Go live info'
                variant='ghost'
                size='xs'
                icon={
                  <Icon viewBox='0 0 24 24' boxSize='5'>
                    <path fill='currentColor' d={mdiInformationOutline} />
                  </Icon>
                }
              />
            </PopoverTrigger>
            <PopoverContent w='64'>
              <PopoverCloseButton />
              <PopoverHeader fontWeight='semibold' borderBottomWidth='0'>
                Go-live date
              </PopoverHeader>
              <PopoverBody>
                <VStack align='stretch' spacing='2'>
                  <Text fontSize='sm'>
                    For more details about the go-live date, visit{' '}
                    <Link
                      href='https://doc.sitecore.com/portal/en/developers/sitecore-cloud-portal/set-a-go-live-date-for-your-site.html'
                      isExternal
                    >
                      Knowledge Base
                    </Link>
                    .
                  </Text>
                  <Text fontSize='sm'>
                    To request a change to the go-live date, visit the{' '}
                    <Link href='https://support.sitecore.com' isExternal>
                      Sitecore Support Portal
                    </Link>
                    .
                  </Text>
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </HStack>
        <Text fontSize='sm'>{dynamicText}</Text>
        <Skeleton isLoaded={!loading} borderRadius='md'>
          <Tooltip
            label={
              <Text fontWeight='normal'>
                {submittedBy ? `Submitted by ${submittedBy}` : formattedDate}
              </Text>
            }
            placement='bottom-start'
            borderRadius='md'
          >
            <HStack spacing='2' w='fit-content' cursor='default'>
              <Icon viewBox='0 0 24 24' boxSize='5' color='primary.500'>
                <path fill='currentColor' d={mdiCalendarOutline} />
              </Icon>
              <Text color='primary.500' fontWeight='semibold' fontSize='sm'>
                {formattedDate}
              </Text>
            </HStack>
          </Tooltip>
        </Skeleton>
      </VStack>
    </Box>
  );
};
